/**
 * Back-office `nav-composition`: one entry per month, `date` as `YYYY-MM`. `value` is in the fund's
 * currency; `xaut` is the same item in gold, set for Goldstream only.
 */
export interface NavCompositionContent {
  entries?: { date?: string; items?: { label?: string; value?: number; xaut?: number }[] }[]
}

/** Which amount of each item a breakdown reads. */
export type NavAmount = 'value' | 'xaut'

export interface NavSlice {
  label: string
  value: number
  /** Share of the month's total, in %. */
  share: number
  color: string
}

export interface NavMonth {
  /** `YYYY-MM`, the entry's key. */
  date: string
  /** UTC ms of the month's first day, for formatting. */
  time: number
  total: number
  slices: NavSlice[]
}

/** Production's palette, in order; further slices get generated hues. */
const PALETTE = ['#2e86c1', '#e67e22', '#8b5cf6', '#3aa757', '#c0392b', '#17a2b8', '#16a085', '#1abc9c', '#7f8c8d']

export const sliceColor = (index: number): string =>
  PALETTE[index] ?? `hsl(${Math.round((index * 137.5) % 360)}, 55%, 50%)`

const MONTH = /^(\d{4})-(\d{2})$/

/**
 * Valid months, newest first: the first is "latest", the rest are the history. An item without
 * the amount is left out; it keeps its colour slot, so both currencies colour an item alike.
 */
export const toNavMonths = (content: NavCompositionContent | undefined, amount: NavAmount = 'value'): NavMonth[] =>
  (content?.entries ?? [])
    .flatMap((entry) => {
      const match = MONTH.exec(entry?.date?.trim() ?? '')
      if (!match) return []
      const slices = (entry.items ?? []).flatMap((item, index) => {
        const label = item?.label?.trim()
        const value = item?.[amount]
        return label && typeof value === 'number' && Number.isFinite(value)
          ? [{ label, value, color: sliceColor(index) }]
          : []
      })
      if (slices.length === 0) return []
      const total = slices.reduce((sum, slice) => sum + slice.value, 0)
      return [
        {
          date: match[0],
          time: Date.UTC(Number(match[1]), Number(match[2]) - 1, 1),
          total,
          slices: slices.map((slice) => ({ ...slice, share: total ? (slice.value / total) * 100 : 0 })),
        },
      ]
    })
    .sort((a, b) => b.time - a.time)

/** One currency's breakdown by month. */
export interface NavComposition {
  currency: string
  months: NavMonth[]
}

/** Gold, the default, before the fund's currency, as the fund's chart: only those with months. */
export const toNavCompositions = (
  content: NavCompositionContent | undefined,
  currency: string,
  goldCurrency: string,
): NavComposition[] =>
  [
    { currency: goldCurrency, months: toNavMonths(content, 'xaut') },
    { currency, months: toNavMonths(content, 'value') },
  ].filter((composition) => composition.months.length > 0)

/** The breakdown in `currency`, else the default (first). */
export const pickNavComposition = (
  compositions: NavComposition[],
  currency: string | undefined,
): NavComposition | undefined =>
  compositions.find((composition) => composition.currency.toLowerCase() === currency?.toLowerCase()) ?? compositions[0]

/** Every month's composition as CSV rows, oldest first: `Month,Item,Value (<currency>),Share (%)`. */
export const toCompositionRows = (months: NavMonth[], currency: string): (string | number)[][] => [
  ['Month', 'Item', `Value (${currency})`, 'Share (%)'],
  ...[...months]
    .reverse()
    .flatMap((month) =>
      month.slices.map((slice) => [month.date, slice.label, slice.value, Math.round(slice.share * 100) / 100]),
    ),
]
